import { useState, useRef, useCallback } from 'react'
import { FormSelect } from '@/components/ui/form'
import { TrashIcon, PlusIcon, TruckIcon } from '@heroicons/react/24/outline'
import clsx from 'clsx'

// ─── Types ───────────────────────────────────────────────

interface Pallet {
  id: number
  weight: number
  cartons: number
  dimensions: string | null
}

interface IncomingShipment {
  id: number
  warehouse_id: number | null
  tracking_number: string | null
  carrier_id: number | null
  freight_invoice_uploaded: boolean
  date_routed: string | null
  incoming_ship_date: string | null
  arrival_date: string | null
  receiving_details: string | null
  weight: string | null
  worked_on_order: string | null
  count_pallets: number
  pallets: Pallet[]
}

interface OutgoingShipment {
  id: number
  ship_out_date: string | null
  terms_and_conditions: string | null
  incoterms: string | null
  carrier_id: number | null
  tracking_number: string | null
  bol_uploaded: boolean
  packed_and_ready_to_ship: boolean
  count_cartons: number
  count_pallets: number
  weight: string | null
  dimensions: string | null
  // Free-text "boxes that aren't on pallets" notes — stored in the
  // OutgoingShipment.boxes_info jsonb column via store_attribute prefix.
  boxes_info_weights: string | null
  boxes_info_dimensions: string | null
  pallets: Pallet[]
}

interface SelectOption { id: number; name: string }

interface ShipmentTabProps {
  incomingShipment: IncomingShipment | null
  outgoingShipment: OutgoingShipment | null
  warehouses: SelectOption[]
  carriers: SelectOption[]
  incoterms: [string, string][]
  csrfToken: string
  currencySymbol: string
  onNotify: (msg: string) => void
}

// ─── Helpers ─────────────────────────────────────────────

const FIELD_LABELS: Record<string, string> = {
  warehouse_id: 'Warehouse', tracking_number: 'Tracking #', carrier_id: 'Carrier',
  freight_invoice_uploaded: 'Freight Invoice Uploaded', date_routed: 'Date Routed',
  incoming_ship_date: 'Ship Date', arrival_date: 'Arrival Date',
  receiving_details: 'Pallet Zone', weight: 'Weight', worked_on_order: 'Worked On Order',
  count_pallets: 'Pallets', ship_out_date: 'Ship Out Date',
  terms_and_conditions: 'Terms & Conditions', incoterms: 'Incoterms',
  bol_uploaded: 'BOL Uploaded', packed_and_ready_to_ship: 'Packed & Ready',
  count_cartons: 'Cartons', dimensions: 'Dimensions',
}

function toRailsDate(iso: string) {
  const [y, m, d] = iso.split('-')
  return y && m && d ? `${m}/${d}/${y}` : iso
}

const BOOL_OPTIONS = [{ value: 'false', label: 'No' }, { value: 'true', label: 'Yes' }]

// ─── Field Row ───────────────────────────────────────────

function ShipmentField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[140px_1fr] items-center gap-2 py-1">
      <span className="text-xs font-medium text-zinc-600 dark:text-zinc-300">{label}</span>
      {children}
    </div>
  )
}

// ─── Section Card ────────────────────────────────────────

function ShipmentSection({ title, color, children }: { title: string; color: string; children: React.ReactNode }) {
  const colors: Record<string, { text: string; iconFg: string; iconBg: string }> = {
    sky: { text: 'text-sky-600 dark:text-sky-400', iconFg: 'text-sky-700 dark:text-sky-400', iconBg: 'bg-sky-50 dark:bg-sky-500/10' },
    emerald: { text: 'text-emerald-600 dark:text-emerald-400', iconFg: 'text-emerald-700 dark:text-emerald-400', iconBg: 'bg-emerald-50 dark:bg-emerald-500/10' },
  }
  const c = colors[color] || colors.sky
  return (
    <div className="mb-5 overflow-hidden rounded-lg bg-white shadow-sm ring-1 ring-zinc-950/5 dark:bg-zinc-900 dark:ring-white/10">
      <div className="p-4">
        <div className="flex items-center gap-3">
          <span className={clsx('inline-flex shrink-0 rounded-md p-1.5', c.iconBg, c.iconFg)}><TruckIcon className="h-4 w-4" /></span>
          <h3 className={clsx('text-base font-semibold', c.text)}>{title}</h3>
        </div>
      </div>
      <div className="divide-y divide-zinc-100 border-t border-zinc-100 px-4 pt-1 pb-2 dark:divide-zinc-800 dark:border-zinc-800">
        {children}
      </div>
    </div>
  )
}

// ─── Pallets Table ───────────────────────────────────────

function PalletsTable({ pallets, shipmentId, shipmentType, csrfToken, onNotify, onAdd, onDelete }: {
  pallets: Pallet[]
  shipmentId: number
  shipmentType: string
  csrfToken: string
  onNotify: (msg: string) => void
  onAdd: (p: Pallet) => void
  onDelete: (id: number) => void
}) {
  const [addWeight, setAddWeight] = useState('')
  const [addCartons, setAddCartons] = useState('')
  const [addDims, setAddDims] = useState('')

  const addPallet = async () => {
    if (!addWeight) return
    const res = await fetch('/pallets.json', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
      body: JSON.stringify({ pallet: { weight: addWeight, cartons: addCartons || 0, dimensions: addDims, shipment_id: shipmentId, shipment_type: shipmentType } }),
    })
    if (res.ok) {
      const data = await res.json()
      onAdd(data)
      setAddWeight(''); setAddCartons(''); setAddDims('')
      onNotify('Saved:Pallet added')
    }
  }

  const deletePallet = async (id: number) => {
    if (!confirm('Delete this pallet?')) return
    const res = await fetch(`/pallets/${id}.json`, { method: 'DELETE', headers: { 'X-CSRF-Token': csrfToken } })
    if (res.ok) { onDelete(id); onNotify('Deleted:Pallet removed') }
  }

  return (
    <div className="mt-2">
      {pallets.length > 0 && (
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-zinc-200 text-center text-[10px] font-semibold uppercase text-zinc-500 dark:border-zinc-700">
              <th className="py-1 pr-2">#</th>
              <th className="py-1 pr-2">Weight</th>
              <th className="py-1 pr-2">Cartons</th>
              <th className="py-1 pr-2">Dimensions</th>
              <th className="py-1 w-6"></th>
            </tr>
          </thead>
          <tbody>
            {pallets.map((p, i) => (
              <tr key={p.id} className="group border-b border-zinc-100 text-center dark:border-zinc-800">
                <td className="py-1 pr-2 text-zinc-500">{i + 1}</td>
                <td className="py-1 pr-2">{p.weight}</td>
                <td className="py-1 pr-2">{p.cartons}</td>
                <td className="py-1 pr-2">{p.dimensions || '—'}</td>
                <td className="py-1">
                  <button onClick={() => deletePallet(p.id)} className="hidden rounded-md border border-zinc-200 p-0.5 text-zinc-400 hover:border-red-200 hover:text-red-500 group-hover:inline dark:border-zinc-700">
                    <TrashIcon className="h-3 w-3" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <div className="mt-2 flex items-center gap-2">
        <input type="number" placeholder="Weight *" value={addWeight} onChange={(e) => setAddWeight(e.target.value)}
          className="w-20 rounded border border-zinc-200 px-1.5 py-1 text-xs dark:border-zinc-700 dark:bg-zinc-800 dark:text-white" />
        <input type="number" placeholder="Cartons" value={addCartons} onChange={(e) => setAddCartons(e.target.value)}
          className="w-20 rounded border border-zinc-200 px-1.5 py-1 text-xs dark:border-zinc-700 dark:bg-zinc-800 dark:text-white" />
        <input type="text" placeholder="Dimensions" value={addDims} onChange={(e) => setAddDims(e.target.value)}
          className="w-28 rounded border border-zinc-200 px-1.5 py-1 text-xs dark:border-zinc-700 dark:bg-zinc-800 dark:text-white" />
        <button onClick={addPallet} disabled={!addWeight}
          className="rounded-md bg-indigo-600 p-1.5 text-white hover:bg-indigo-500 disabled:opacity-40">
          <PlusIcon className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  )
}

// ─── Main Component ──────────────────────────────────────

export default function ShipmentTab({ incomingShipment, outgoingShipment, warehouses, carriers, incoterms, csrfToken, onNotify }: ShipmentTabProps) {
  const [incoming, setIncoming] = useState(incomingShipment)
  const [outgoing, setOutgoing] = useState(outgoingShipment)
  const savedRef = useRef<Record<string, string>>({})

  const saveIncoming = useCallback((field: string, value: string) => {
    if (!incoming) return
    const key = `in_${field}`
    if (savedRef.current[key] === value) return
    const sendVal = ['date_routed', 'incoming_ship_date', 'arrival_date'].includes(field) && value ? toRailsDate(value) : value
    fetch(`/incoming_shipments/${incoming.id}.json`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
      body: JSON.stringify({ incoming_shipment: { [field]: sendVal } }),
    }).then(res => {
      if (res.ok) {
        savedRef.current[key] = value
        setIncoming(prev => prev ? { ...prev, [field]: value } : prev)
        onNotify('Saved:' + (FIELD_LABELS[field] || field))
      }
    })
  }, [incoming?.id, csrfToken, onNotify])

  const saveOutgoing = useCallback((field: string, value: string) => {
    if (!outgoing) return
    const key = `out_${field}`
    if (savedRef.current[key] === value) return
    const sendVal = field === 'ship_out_date' && value ? toRailsDate(value) : value
    fetch(`/outgoing_shipments/${outgoing.id}.json`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
      body: JSON.stringify({ outgoing_shipment: { [field]: sendVal } }),
    }).then(res => {
      if (res.ok) {
        savedRef.current[key] = value
        setOutgoing(prev => prev ? { ...prev, [field]: value } : prev)
        onNotify('Saved:' + (FIELD_LABELS[field] || field))
      }
    })
  }, [outgoing?.id, csrfToken, onNotify])

  const hasPalletsIn = (incoming?.pallets?.length ?? 0) > 0
  const hasPalletsOut = (outgoing?.pallets?.length ?? 0) > 0

  return (
    <div data-testid="shipment-tab" className="grid grid-cols-1 gap-x-8 lg:grid-cols-2">
      {/* ── Incoming ── */}
      <div>
        <ShipmentSection title="Incoming Shipment" color="sky">
          {incoming ? (
            <>
              <ShipmentField label="Warehouse">
                <FormSelect value={String(incoming.warehouse_id || '')} placeholder="Select..."
                  onChange={(v) => saveIncoming('warehouse_id', v)}
                  options={warehouses.map(w => ({ value: String(w.id), label: w.name }))} />
              </ShipmentField>
              <ShipmentField label="Tracking # / BOL #">
                <input name="tracking_number" value={incoming.tracking_number || ''} onChange={(e) => setIncoming(prev => prev ? { ...prev, tracking_number: e.target.value } : prev)}
                  onBlur={(e) => saveIncoming('tracking_number', e.target.value)}
                  className="w-full rounded-lg border border-zinc-950/10 bg-transparent px-2.5 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-white/10 dark:bg-white/5 dark:text-white" />
              </ShipmentField>
              <ShipmentField label="Carrier">
                <FormSelect value={String(incoming.carrier_id || '')} placeholder="Select..."
                  onChange={(v) => saveIncoming('carrier_id', v)}
                  options={carriers.map(c => ({ value: String(c.id), label: c.name }))} />
              </ShipmentField>
              <ShipmentField label="Freight Invoice Uploaded?">
                <FormSelect value={String(incoming.freight_invoice_uploaded)} onChange={(v) => saveIncoming('freight_invoice_uploaded', v)} options={BOOL_OPTIONS} />
              </ShipmentField>
              <ShipmentField label="Date Routed">
                <input type="date" value={incoming.date_routed || ''} onChange={(e) => { setIncoming(prev => prev ? { ...prev, date_routed: e.target.value } : prev); if (e.target.value) saveIncoming('date_routed', e.target.value) }}
                  className="w-full rounded-lg border border-zinc-950/10 bg-transparent px-2.5 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-white/10 dark:bg-white/5 dark:text-white" />
              </ShipmentField>
              <ShipmentField label="Incoming Ship Date">
                <input type="date" value={incoming.incoming_ship_date || ''} onChange={(e) => { setIncoming(prev => prev ? { ...prev, incoming_ship_date: e.target.value } : prev); if (e.target.value) saveIncoming('incoming_ship_date', e.target.value) }}
                  className="w-full rounded-lg border border-zinc-950/10 bg-transparent px-2.5 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-white/10 dark:bg-white/5 dark:text-white" />
              </ShipmentField>
              <ShipmentField label="Arrival Date">
                <input type="date" value={incoming.arrival_date || ''} onChange={(e) => { setIncoming(prev => prev ? { ...prev, arrival_date: e.target.value } : prev); if (e.target.value) saveIncoming('arrival_date', e.target.value) }}
                  className="w-full rounded-lg border border-zinc-950/10 bg-transparent px-2.5 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-white/10 dark:bg-white/5 dark:text-white" />
              </ShipmentField>
              <ShipmentField label="Pallet Zone">
                <input name="receiving_details" value={incoming.receiving_details || ''} onChange={(e) => setIncoming(prev => prev ? { ...prev, receiving_details: e.target.value } : prev)}
                  onBlur={(e) => saveIncoming('receiving_details', e.target.value)}
                  className="w-full rounded-lg border border-zinc-950/10 bg-transparent px-2.5 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-white/10 dark:bg-white/5 dark:text-white" />
              </ShipmentField>
              <ShipmentField label="Weight, LBS">
                <input name="weight" type="number" value={incoming.weight || ''} disabled={hasPalletsIn}
                  onChange={(e) => setIncoming(prev => prev ? { ...prev, weight: e.target.value } : prev)}
                  onBlur={(e) => saveIncoming('weight', e.target.value)}
                  className="w-full rounded-lg border border-zinc-950/10 bg-transparent px-2.5 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50 dark:border-white/10 dark:bg-white/5 dark:text-white" />
              </ShipmentField>
              <ShipmentField label="Worked On Order">
                <input name="worked_on_order" value={incoming.worked_on_order || ''} onChange={(e) => setIncoming(prev => prev ? { ...prev, worked_on_order: e.target.value } : prev)}
                  onBlur={(e) => saveIncoming('worked_on_order', e.target.value)}
                  className="w-full rounded-lg border border-zinc-950/10 bg-transparent px-2.5 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-white/10 dark:bg-white/5 dark:text-white" />
              </ShipmentField>
              <ShipmentField label="Pallets">
                <input name="count_pallets" type="number" value={incoming.count_pallets || 0} disabled={hasPalletsIn}
                  onChange={(e) => setIncoming(prev => prev ? { ...prev, count_pallets: Number(e.target.value) } : prev)}
                  onBlur={(e) => saveIncoming('count_pallets', e.target.value)}
                  className="w-full rounded-lg border border-zinc-950/10 bg-transparent px-2.5 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50 dark:border-white/10 dark:bg-white/5 dark:text-white" />
              </ShipmentField>
            </>
          ) : (
            <p className="py-3 text-xs text-zinc-400">No incoming shipment</p>
          )}
        </ShipmentSection>
      </div>

      {/* ── Outgoing ── */}
      <div>
        <ShipmentSection title="Outgoing Shipment" color="emerald">
          {outgoing ? (
            <>
              <ShipmentField label="Ship Out Date">
                <input type="date" value={outgoing.ship_out_date || ''} onChange={(e) => { setOutgoing(prev => prev ? { ...prev, ship_out_date: e.target.value } : prev); if (e.target.value) saveOutgoing('ship_out_date', e.target.value) }}
                  className="w-full rounded-lg border border-zinc-950/10 bg-transparent px-2.5 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-white/10 dark:bg-white/5 dark:text-white" />
              </ShipmentField>
              <ShipmentField label="Terms & Conditions">
                <input value={outgoing.terms_and_conditions || ''} onChange={(e) => setOutgoing(prev => prev ? { ...prev, terms_and_conditions: e.target.value } : prev)}
                  onBlur={(e) => saveOutgoing('terms_and_conditions', e.target.value)}
                  className="w-full rounded-lg border border-zinc-950/10 bg-transparent px-2.5 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-white/10 dark:bg-white/5 dark:text-white" />
              </ShipmentField>
              <ShipmentField label="Incoterms">
                <FormSelect value={outgoing.incoterms || ''} placeholder="Select..."
                  onChange={(v) => saveOutgoing('incoterms', v)}
                  options={incoterms.map(([k]) => ({ value: k, label: k }))} />
              </ShipmentField>
              <ShipmentField label="Carrier">
                <FormSelect value={String(outgoing.carrier_id || '')} placeholder="Select..."
                  onChange={(v) => saveOutgoing('carrier_id', v)}
                  options={carriers.map(c => ({ value: String(c.id), label: c.name }))} />
              </ShipmentField>
              <ShipmentField label="Tracking # / BOL #">
                <input value={outgoing.tracking_number || ''} onChange={(e) => setOutgoing(prev => prev ? { ...prev, tracking_number: e.target.value } : prev)}
                  onBlur={(e) => saveOutgoing('tracking_number', e.target.value)}
                  className="w-full rounded-lg border border-zinc-950/10 bg-transparent px-2.5 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-white/10 dark:bg-white/5 dark:text-white" />
              </ShipmentField>
              <ShipmentField label="Packed & Ready?">
                <FormSelect value={String(outgoing.packed_and_ready_to_ship)} onChange={(v) => saveOutgoing('packed_and_ready_to_ship', v)} options={BOOL_OPTIONS} />
              </ShipmentField>
              <ShipmentField label="BOL Uploaded?">
                <FormSelect value={String(outgoing.bol_uploaded)} onChange={(v) => saveOutgoing('bol_uploaded', v)} options={BOOL_OPTIONS} />
              </ShipmentField>
              <ShipmentField label="Cartons">
                <input type="number" value={outgoing.count_cartons || 0} disabled
                  className="w-full rounded-lg border border-zinc-950/10 bg-transparent px-2.5 py-1 text-xs opacity-50 dark:border-white/10 dark:bg-white/5 dark:text-white" />
              </ShipmentField>
              <ShipmentField label="Pallets">
                <input type="number" value={outgoing.count_pallets || 0} disabled={hasPalletsOut}
                  onChange={(e) => setOutgoing(prev => prev ? { ...prev, count_pallets: Number(e.target.value) } : prev)}
                  onBlur={(e) => saveOutgoing('count_pallets', e.target.value)}
                  className="w-full rounded-lg border border-zinc-950/10 bg-transparent px-2.5 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50 dark:border-white/10 dark:bg-white/5 dark:text-white" />
              </ShipmentField>
              <ShipmentField label="Weight, LBS">
                <input type="number" value={hasPalletsOut ? outgoing.pallets.reduce((s, p) => s + p.weight, 0) : (outgoing.weight || '')} disabled={hasPalletsOut}
                  onChange={(e) => setOutgoing(prev => prev ? { ...prev, weight: e.target.value } : prev)}
                  onBlur={(e) => saveOutgoing('weight', e.target.value)}
                  className="w-full rounded-lg border border-zinc-950/10 bg-transparent px-2.5 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50 dark:border-white/10 dark:bg-white/5 dark:text-white" />
              </ShipmentField>
              {!hasPalletsOut && (
                <ShipmentField label="Dimensions">
                  <input value={outgoing.dimensions || ''} onChange={(e) => setOutgoing(prev => prev ? { ...prev, dimensions: e.target.value } : prev)}
                    onBlur={(e) => saveOutgoing('dimensions', e.target.value)}
                    className="w-full rounded-lg border border-zinc-950/10 bg-transparent px-2.5 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-white/10 dark:bg-white/5 dark:text-white" />
                </ShipmentField>
              )}
            </>
          ) : (
            <p className="py-3 text-xs text-zinc-400">No outgoing shipment</p>
          )}
        </ShipmentSection>
      </div>

      {/* ── Pallets: Incoming (left) / Outgoing (right) ── */}
      {incoming && (
        <div>
          <ShipmentSection title="Incoming Pallets" color="sky">
            <PalletsTable pallets={incoming.pallets} shipmentId={incoming.id} shipmentType="IncomingShipment"
              csrfToken={csrfToken} onNotify={onNotify}
              onAdd={(p) => setIncoming(prev => prev ? { ...prev, pallets: [...prev.pallets, p], count_pallets: prev.pallets.length + 1, weight: String(prev.pallets.reduce((s, x) => s + x.weight, 0) + p.weight) } : prev)}
              onDelete={(id) => setIncoming(prev => {
                if (!prev) return prev
                const kept = prev.pallets.filter(p => p.id !== id)
                return { ...prev, pallets: kept, count_pallets: kept.length, weight: String(kept.reduce((s, x) => s + x.weight, 0)) }
              })} />
          </ShipmentSection>
        </div>
      )}
      {outgoing && (
        <div>
          <ShipmentSection title="Outgoing Pallets" color="emerald">
            <PalletsTable pallets={outgoing.pallets} shipmentId={outgoing.id} shipmentType="OutgoingShipment"
              csrfToken={csrfToken} onNotify={onNotify}
              onAdd={(p) => setOutgoing(prev => prev ? { ...prev, pallets: [...prev.pallets, p], count_pallets: prev.pallets.length + 1, weight: String(prev.pallets.reduce((s, x) => s + x.weight, 0) + p.weight) } : prev)}
              onDelete={(id) => setOutgoing(prev => {
                if (!prev) return prev
                const kept = prev.pallets.filter(p => p.id !== id)
                return { ...prev, pallets: kept, count_pallets: kept.length, weight: String(kept.reduce((s, x) => s + x.weight, 0)) }
              })} />
          </ShipmentSection>
        </div>
      )}

      {/* ── Boxes Without Pallets — free-text weights + dimensions for ──
          loose boxes that aren't on a pallet. Saved to OutgoingShipment
          .boxes_info jsonb on blur, matching the legacy haml block. */}
      {outgoing && (
        <div data-testid="boxes-without-pallets">
          <ShipmentSection title="Boxes Without Pallets" color="amber">
            <ShipmentField label="Weights">
              <input
                value={outgoing.boxes_info_weights || ''}
                placeholder="Weight"
                onChange={(e) => setOutgoing(prev => prev ? { ...prev, boxes_info_weights: e.target.value } : prev)}
                onBlur={(e) => saveOutgoing('boxes_info_weights', e.target.value)}
                data-testid="boxes-weights-input"
                className="w-full rounded-lg border border-zinc-950/10 bg-transparent px-2.5 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-white/10 dark:bg-white/5 dark:text-white"
              />
            </ShipmentField>
            <ShipmentField label="Dimensions">
              <input
                value={outgoing.boxes_info_dimensions || ''}
                placeholder="Dimensions"
                onChange={(e) => setOutgoing(prev => prev ? { ...prev, boxes_info_dimensions: e.target.value } : prev)}
                onBlur={(e) => saveOutgoing('boxes_info_dimensions', e.target.value)}
                data-testid="boxes-dimensions-input"
                className="w-full rounded-lg border border-zinc-950/10 bg-transparent px-2.5 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-white/10 dark:bg-white/5 dark:text-white"
              />
            </ShipmentField>
          </ShipmentSection>
        </div>
      )}
    </div>
  )
}
